import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { InsightSuggestion, InsightsSummary } from '@financial/shared';
import { useAuthStore } from '../store/auth';
import { useInsights } from '../features/insights/useInsights';
import { useAccounts } from '../features/accounts/useAccounts';
import { useCard } from '../features/creditCard/useCreditCard';
import { useLoans } from '../features/loans/useLoans';
import { useProfile } from '../features/profile/useProfile';
import { useUpcomingRecurring } from '../features/recurring/useRecurring';
import { useSavings } from '../features/savings/useSavings';
import { useTransactions } from '../features/transactions/useTransactions';
import { GuidedPageHeader } from '../components/onboarding/GuidedPageHeader';
import { GettingStartedChecklist } from '../components/onboarding/GettingStartedChecklist';
import { currentCurrency, formatDate, formatMoney, setActiveCurrency } from '../lib/format';
import { type AccountWithBuckets, type Loan, type UpcomingRecurring } from '@financial/shared';
import {
  BudgetGauge,
  CategoryBudgetBars,
  ChartCard,
  FinancialTrendChart,
  GoalRadial,
  NetWorthDonut,
} from '../components/charts/DashboardCharts';
import type { FinancialTrendMode } from '../components/charts/DashboardCharts';
import { FinancialPulsePanel } from '../components/dashboard/FinancialPulsePanel';
import { ResponsiveWaterfall } from '../components/dashboard/ResponsiveWaterfall';
import { MonthPicker } from '../components/ui/DatePicker';
import { Button, Skeleton } from '../components/ui/Primitives';
import { SkeletonState } from '../components/ui/SkeletonState';
import { currentYearMonth } from '../lib/businessDate';

function nextYearMonth(month: string): string {
  const [year, rawMonth] = month.split('-').map(Number);
  const next = new Date(year, rawMonth, 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(month: string, short = false): string {
  const [year, rawMonth] = month.split('-');
  const date = new Date(Number(year), Number(rawMonth) - 1, 1);
  const name = new Intl.DateTimeFormat('es-MX', { month: short ? 'short' : 'long' }).format(date);
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`;
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const [month, setMonth] = useState(currentYearMonth());
  const [currency, setCurrency] = useState(currentCurrency());
  const { data, isLoading, isError, isFetching, refetch } = useInsights(month, currency);
  const accountsQ = useAccounts();
  const cardQ = useCard();
  const profileQ = useProfile();
  const firstTransactionQ = useTransactions({ limit: 1 });
  const savingsQ = useSavings();
  const loansQ = useLoans();
  // Lo pendiente lo deriva el backend: respeta los ajustes por mes y no lista
  // lo que ya se registró este mes.
  // Para el horizonte de 30 días se consulta el mes actual y el siguiente;
  // después la tarjeta filtra por fecha exacta, no por "mes calendario".
  const currentMonth = currentYearMonth();
  const upcomingCurrentQ = useUpcomingRecurring(currentMonth);
  const upcomingNextQ = useUpcomingRecurring(nextYearMonth(currentMonth));
  const availableCurrencies = Array.from(
    new Set((accountsQ.data ?? []).map((account) => account.currency)),
  );
  if (availableCurrencies.length === 0) availableCurrencies.push(user?.defaultCurrency ?? currency);
  // El primer uso sólo representa un vacío confirmado. Si el resumen falla,
  // el error tiene prioridad aunque todavía no haya cuentas.
  const isFirstUse = !isError && accountsQ.isSuccess && (accountsQ.data?.length ?? 0) === 0;
  const hasEstimatedIncome = Object.values(profileQ.data?.savingsProfiles ?? {}).some(
    (profile) => (profile?.baseIncome ?? 0) > 0,
  );
  const hasAiConsent = profileQ.data?.aiConsentGranted === true;
  const gettingStartedSteps = [
    {
      id: 'account',
      title: 'Primera cuenta lista',
      description: 'Revisa Efectivo si aparece o agrega una cuenta, y concilia su saldo de hoy.',
      to: '/cuentas',
      complete: (accountsQ.data?.length ?? 0) > 0,
      loading: accountsQ.isLoading,
      error: accountsQ.isError && accountsQ.data === undefined,
    },
    {
      id: 'assistant',
      title: 'Activa el asistente',
      description: 'Da permiso para usar IA. La prueba se ofrece una vez por cuenta.',
      to: '/ajustes#asistente',
      complete: hasAiConsent,
      loading: profileQ.isLoading,
      error: profileQ.isError && profileQ.data === undefined,
    },
    {
      id: 'movement',
      title: 'Cuenta tu primer gasto',
      description: 'Escríbelo como pasó. Tú revisas antes de guardar.',
      to: '/asistente',
      complete: (firstTransactionQ.data?.length ?? 0) > 0,
      loading: firstTransactionQ.isLoading,
      error: firstTransactionQ.isError && firstTransactionQ.data === undefined,
    },
    {
      id: 'income',
      title: 'Estima tu ingreso habitual',
      description: 'Ayuda a calcular tus límites y tu ahorro.',
      to: '/ajustes#ahorro',
      complete: hasEstimatedIncome,
      loading: profileQ.isLoading,
      error: profileQ.isError && profileQ.data === undefined,
    },
    {
      id: 'card',
      title: 'Configura tu tarjeta',
      description: 'Registra fechas y compras que aún pagas.',
      to: '/tarjeta?accion=configurar',
      complete: cardQ.data !== null && cardQ.data !== undefined,
      loading: cardQ.isLoading,
      error: cardQ.isError && cardQ.data === undefined,
    },
    {
      id: 'goal',
      title: 'Crea una meta de ahorro',
      description: 'Define para qué quieres separar dinero.',
      to: '/metas?nuevo=1',
      complete: (savingsQ.data?.length ?? 0) > 0,
      loading: savingsQ.isLoading,
      error: savingsQ.isError && savingsQ.data === undefined,
    },
  ];

  return (
    <div className="reference-page dashboard-page w-full">
      <GuidedPageHeader
        section="dashboard"
        eyebrow={user?.name ? `Hola, ${user.name.split(' ')[0]}` : undefined}
        title="Tu panorama"
        description={`${monthLabel(month)} · lo que entró, salió y podría quedarte.`}
        actions={
          isFirstUse ? undefined : (
            <div className="flex items-center gap-2">
              {availableCurrencies.length > 1 && (
                <select
                  aria-label="Moneda del panorama"
                  value={currency}
                  onChange={(event) => {
                    const next = event.target.value as typeof currency;
                    setActiveCurrency(next);
                    setCurrency(next);
                  }}
                  className="tap-target rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm font-semibold text-slate-700"
                >
                  {availableCurrencies.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              )}
              <MonthPicker
                value={month}
                onChange={(nextMonth) => setMonth(nextMonth || currentYearMonth())}
              />
            </div>
          )
        }
      />

      {!isError && accountsQ.isSuccess && (
        <GettingStartedChecklist userId={user?.id} steps={gettingStartedSteps} />
      )}

      {isError ? (
        <DashboardLoadError isRetrying={isFetching} onRetry={() => void refetch()} />
      ) : isFirstUse ? (
        <FirstUseDashboard />
      ) : isLoading ? (
        <DashboardSkeleton />
      ) : data ? (
        <DashboardContent
          data={data}
          accounts={accountsQ.data ?? []}
          loans={loansQ.data ?? []}
          upcoming={[...(upcomingCurrentQ.data?.items ?? []), ...(upcomingNextQ.data?.items ?? [])]}
          accountsState={
            accountsQ.data !== undefined ? 'ready' : accountsQ.isError ? 'error' : 'loading'
          }
          loansState={loansQ.data !== undefined ? 'ready' : loansQ.isError ? 'error' : 'loading'}
          upcomingState={
            upcomingCurrentQ.data !== undefined && upcomingNextQ.data !== undefined
              ? 'ready'
              : upcomingCurrentQ.isError || upcomingNextQ.isError
                ? 'error'
                : 'loading'
          }
        />
      ) : null}
    </div>
  );
}

function DashboardLoadError({ isRetrying, onRetry }: { isRetrying: boolean; onRetry: () => void }) {
  return (
    <section
      role="alert"
      aria-labelledby="dashboard-load-error-title"
      className="mt-6 rounded-[16px] border border-red-200 bg-red-50 p-4 sm:p-5"
    >
      <h2 id="dashboard-load-error-title" className="text-sm font-bold text-red-900">
        No pudimos cargar tu panorama
      </h2>
      <p className="mt-1 text-sm leading-6 text-red-900">
        Algo falló al consultar el resumen de este mes. Tus datos no cambiaron.
      </p>
      <Button
        type="button"
        variant="secondary"
        className="mt-3"
        disabled={isRetrying}
        aria-busy={isRetrying}
        onClick={onRetry}
      >
        Volver a intentar
      </Button>
    </section>
  );
}

function FirstUseDashboard() {
  return (
    <section className="mx-auto max-w-md" aria-labelledby="first-use-title">
      <div className="rounded-[18px] border border-dashed border-slate-200 bg-white px-5 py-6 text-center shadow-[0_1px_2px_rgba(15,23,42,.04),0_10px_24px_-18px_rgba(15,23,42,.2)]">
        <span
          className="mx-auto flex h-10 w-10 items-center justify-center rounded-[14px] bg-slate-100 text-slate-400"
          aria-hidden
        >
          ▥
        </span>
        <h2 id="first-use-title" className="mt-3 text-sm font-semibold text-slate-700">
          Tu panorama aparecerá aquí
        </h2>
        <p className="mt-1 text-xs leading-5 text-slate-600">
          Cuando registres tu primera cuenta y algunos movimientos verás el resumen y las gráficas
          de tu mes.
        </p>
        <Link to="/cuentas?nuevo=1" className="button button-primary mt-4">
          <span className="text-lg font-normal leading-none">+</span> Agregar mi primera cuenta
        </Link>
      </div>

      <Link
        to="/cuentas?nuevo=1"
        className="tap-target mt-3 flex items-center justify-between gap-3 rounded-xl bg-blue-50 px-3 text-xs font-semibold leading-5 text-blue-800 hover:bg-blue-100"
      >
        <span>¿Prefieres empezar rápido? Agrega saldos aproximados y ajústalos después.</span>
        <span aria-hidden>›</span>
      </Link>
    </section>
  );
}

function DashboardContent({
  data,
  accounts,
  loans,
  upcoming: upcomingItems,
  accountsState,
  loansState,
  upcomingState,
}: {
  data: InsightsSummary;
  accounts: AccountWithBuckets[];
  loans: Loan[];
  upcoming: UpcomingRecurring[];
  accountsState: 'loading' | 'error' | 'ready';
  loansState: 'loading' | 'error' | 'ready';
  upcomingState: 'loading' | 'error' | 'ready';
}) {
  // Si existe, el fondo de emergencia es la primera meta que debe guiar una
  // decisión de gasto. El enganche u otros objetivos se muestran aparte para
  // no confundir dinero para imprevistos con dinero disponible para vivienda.
  const emergencyGoal = data.goals.find((goal) => /fondo de emergencia/i.test(goal.name));
  const primaryGoal = emergencyGoal ?? data.goals[0];
  const [trendMode, setTrendMode] = useState<FinancialTrendMode>('monthly');
  const activeFlowMonths = data.monthlyFlow.months.filter(
    (item) => item.income !== 0 || item.expense !== 0 || item.net !== 0,
  ).length;
  const topCategory = data.spendingByCategory.slice().sort((a, b) => b.total - a.total)[0];
  const accountsInCurrency = accounts.filter((account) => account.currency === data.currency);
  const actions = data.suggestions.filter(hasSuggestionAction).slice(0, 3);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const showMonthlyCalculation = () => {
    setDetailsOpen(true);
    window.requestAnimationFrame(() => {
      const details = detailsRef.current;
      details?.querySelector('summary')?.focus({ preventScroll: true });
      details?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <>
      {/* La respuesta principal aparece antes que el desglose y las gráficas. */}
      <MonthlyAnswer
        data={data}
        calculationOpen={detailsOpen}
        onShowCalculation={showMonthlyCalculation}
      />

      {actions.length > 0 && (
        <section className="mt-4" aria-labelledby="monthly-actions-heading">
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[.08em] text-slate-500">
                Acciones para este mes
              </p>
              <h2 id="monthly-actions-heading" className="mt-0.5 text-lg font-bold text-slate-900">
                Qué conviene revisar ahora
              </h2>
            </div>
            <span className="text-xs text-slate-500">Hasta 3 prioridades</span>
          </div>
          <ul className="mt-3 grid gap-3 lg:grid-cols-3">
            {actions.map((action) => (
              <MonthlyAction key={`${action.action.to}-${action.message}`} suggestion={action} />
            ))}
          </ul>
        </section>
      )}

      <section className="mt-4" aria-labelledby="registered-summary-heading">
        <div className="mb-2 flex items-center gap-2">
          <h2 id="registered-summary-heading" className="text-sm font-semibold text-slate-700">
            Lo registrado este mes
          </h2>
          <StatusBadge status="Registrado" />
        </div>
        <div className="grid gap-3 sm:grid-cols-3 lg:gap-4">
          <Kpi
            label="Ingresos recibidos"
            value={data.kpis.income}
            tone="income"
            to="/transacciones"
          />
          <Kpi label="Gasto variable" value={data.kpis.expenses} tone="expense" to="/presupuesto" />
          <Kpi
            label="Ahorro del mes"
            value={data.kpis.savingsThisMonth}
            tone="savings"
            to="/metas"
          />
        </div>
      </section>

      {data.balanceAdjustments.count > 0 && (
        <BalanceAdjustmentsSummary summary={data.balanceAdjustments} currency={data.currency} />
      )}

      <FinancialPulsePanel
        data={data}
        accounts={accountsInCurrency}
        loans={loans}
        upcoming={upcomingItems}
        accountsState={accountsState}
        loansState={loansState}
        upcomingState={upcomingState}
      />

      <SavingsCoach
        saved={data.kpis.savingsThisMonth}
        target={data.kpis.savingsTarget}
        income={data.kpis.income}
        goal={primaryGoal}
        topCategory={
          topCategory
            ? `${topCategory.icon ? `${topCategory.icon} ` : ''}${topCategory.name}`
            : undefined
        }
      />

      <details
        id="detalle-calculo-mensual"
        ref={detailsRef}
        open={detailsOpen}
        onToggle={(event) => setDetailsOpen(event.currentTarget.open)}
        className="group mt-5 scroll-mt-4 overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,.06)]"
      >
        <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 marker:content-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset sm:px-5">
          <span>
            <span className="block text-sm font-semibold text-slate-900">
              <span className="group-open:hidden">Ver detalle y gráficas</span>
              <span className="hidden group-open:inline">Ocultar detalle y gráficas</span>
            </span>
            <span className="mt-0.5 block text-xs text-slate-500">
              Desglose del mes, categorías, historial, metas y patrimonio.
            </span>
          </span>
          <span
            className="shrink-0 text-lg text-slate-500 transition-transform group-open:rotate-180"
            aria-hidden="true"
          >
            ⌄
          </span>
        </summary>

        {/* Las gráficas quedan disponibles como detalle sin desplazar las prioridades. */}
        <div className="space-y-4 border-t border-slate-200 bg-slate-50/50 p-3 sm:p-4 lg:space-y-5 lg:p-5">
          <div className="grid items-start gap-3 lg:gap-4 xl:grid-cols-3">
            <ChartCard
              title="¿A dónde va tu dinero?"
              description="Muestra lo que entró, lo que ya está destinado y cuánto podría quedarte al final."
              className="xl:col-span-2"
              to="/presupuesto"
            >
              <ResponsiveWaterfall steps={data.waterfall} />
            </ChartCard>

            <ChartCard
              title="Gasto vs. límite"
              description="Sólo considera gasto variable; pagos, fijos y traspasos van por separado."
              to="/presupuesto"
            >
              <BudgetGauge
                spent={data.budget.variableSpent}
                limit={data.budget.effectiveLimit}
                status={data.budget.status}
              />
            </ChartCard>
          </div>

          <ChartCard
            title="Gasto y dinero restante por categoría"
            description="Compara lo registrado con los límites que configuraste; los rubros sin límite quedan visibles para que decidas si debes asignarles uno."
            to="/presupuesto"
          >
            <CategoryBudgetBars data={data.spendingByCategory} limits={data.budget.byCategory} />
          </ChartCard>

          <ChartCard
            title="Entradas y salidas a través del tiempo"
            description={
              trendMode === 'monthly'
                ? activeFlowMonths < 3
                  ? `Hay ${activeFlowMonths} meses con registros. Incluye compras con tarjeta en su fecha de compra, sin duplicar su pago.`
                  : 'Dinero que entró menos dinero que salió. Incluye cada compra con tarjeta en su fecha y no vuelve a contar su pago.'
                : activeFlowMonths < 3
                  ? `Hay ${activeFlowMonths} meses con registros; es un acumulado, todavía no una tendencia consolidada.`
                  : 'Suma el resultado de cada mes. No representa todo lo que tienes ni el saldo de una sola cuenta.'
            }
            actions={
              <div
                className="flex w-full rounded-lg bg-slate-100 p-0.5 text-xs font-semibold sm:w-auto"
                role="tablist"
                aria-label="Vista de tendencia"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={trendMode === 'monthly'}
                  onClick={() => setTrendMode('monthly')}
                  className={`min-h-11 min-w-0 flex-1 whitespace-nowrap rounded-md px-2 py-1.5 transition-colors sm:flex-none sm:px-2.5 ${trendMode === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
                >
                  Resultado mensual
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={trendMode === 'cumulative'}
                  onClick={() => setTrendMode('cumulative')}
                  className={`min-h-11 min-w-0 flex-1 whitespace-nowrap rounded-md px-2 py-1.5 transition-colors sm:flex-none sm:px-2.5 ${trendMode === 'cumulative' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}
                >
                  Resultado acumulado
                </button>
              </div>
            }
          >
            <FinancialTrendChart months={data.monthlyFlow.months} mode={trendMode} />
          </ChartCard>

          <div className="grid items-start gap-3 lg:gap-4 xl:grid-cols-2">
            <ChartCard
              title="Metas: seguridad y largo plazo"
              description="Primero protege tu fondo para imprevistos; después avanza el enganche sin mezclar ambos saldos."
            >
              {primaryGoal ? (
                <GoalsOverview primaryGoal={primaryGoal} goals={data.goals} />
              ) : (
                <p className="py-8 text-center text-sm text-slate-500">Crea una meta de ahorro.</p>
              )}
            </ChartCard>

            <ChartCard
              title="Dónde está tu dinero"
              description="Separa el dinero disponible del ahorro o inversión y resta tus deudas para mostrar el total de lo que tienes."
              to="/cuentas"
            >
              {accountsState === 'ready' ? (
                <NetWorthDonut netWorth={data.netWorth} accounts={accountsInCurrency} />
              ) : (
                <p
                  className={
                    accountsState === 'error'
                      ? 'py-8 text-center text-sm text-expense'
                      : 'py-8 text-center text-sm text-slate-500'
                  }
                  role="status"
                >
                  {accountsState === 'error'
                    ? 'No pudimos completar el detalle de tus cuentas.'
                    : 'Consultando el detalle de tus cuentas…'}
                </p>
              )}
            </ChartCard>
          </div>
        </div>
      </details>
    </>
  );
}

type SuggestionWithAction = InsightSuggestion & {
  action: { label: string; to: string };
};

function hasSuggestionAction(suggestion: InsightSuggestion): suggestion is SuggestionWithAction {
  if (!('action' in suggestion)) return false;
  const action = suggestion.action;
  return (
    typeof action === 'object' &&
    action !== null &&
    'label' in action &&
    typeof action.label === 'string' &&
    'to' in action &&
    typeof action.to === 'string'
  );
}

function StatusBadge({ status }: { status: 'Registrado' | 'Estimado' }) {
  const style =
    status === 'Registrado'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : 'border-blue-200 bg-blue-50 text-blue-800';
  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[.06em] ${style}`}
    >
      {status}
    </span>
  );
}

function signedMoney(value: number, currency: InsightsSummary['currency']): string {
  if (value === 0) return formatMoney(0, currency);
  return `${value > 0 ? '+' : '−'}${formatMoney(Math.abs(value), currency)}`;
}

/**
 * Una conciliación cambia la fotografía actual de las cuentas, pero no es un
 * ingreso ni un gasto del mes. La mostramos cerca de los hechos registrados sin
 * mezclarla con sus KPIs ni con el presupuesto.
 */
function BalanceAdjustmentsSummary({
  summary,
  currency,
}: {
  summary: InsightsSummary['balanceAdjustments'];
  currency: InsightsSummary['currency'];
}) {
  const reconciliationLabel =
    summary.count === 1
      ? '1 conciliación registrada'
      : `${summary.count} conciliaciones registradas`;

  return (
    <aside
      aria-labelledby="balance-adjustments-heading"
      className="mt-3 rounded-[16px] border border-blue-200 bg-blue-50/70 p-4 sm:p-5"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id="balance-adjustments-heading" className="text-sm font-bold text-slate-900">
              Ajustes de saldo
            </h3>
            <span className="rounded-full border border-blue-200 bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[.06em] text-blue-800">
              No es ingreso ni gasto
            </span>
          </div>
          <p className="mt-1 text-xs font-semibold text-slate-600">{reconciliationLabel}</p>
          <p className="mt-1 max-w-3xl text-sm leading-5 text-slate-600">
            Corrigen diferencias entre la app y tus cuentas. No cuentan como ingresos o gastos ni
            cambian tu presupuesto del mes.
          </p>
        </div>
        <Link
          to="/cuentas"
          className="inline-flex min-h-11 shrink-0 items-center text-sm font-semibold text-blue-700 hover:text-blue-900"
        >
          Revisar cuentas →
        </Link>
      </div>

      <dl className="mt-3 grid overflow-hidden rounded-xl border border-blue-100 bg-white/80 sm:grid-cols-3 sm:divide-x sm:divide-blue-100">
        <AdjustmentMetric label="Saldo que subió" value={summary.increases} currency={currency} />
        <AdjustmentMetric label="Saldo que bajó" value={-summary.decreases} currency={currency} />
        <AdjustmentMetric label="Cambio neto del saldo" value={summary.net} currency={currency} />
      </dl>
    </aside>
  );
}

function AdjustmentMetric({
  label,
  value,
  currency,
}: {
  label: string;
  value: number;
  currency: InsightsSummary['currency'];
}) {
  const tone = value > 0 ? 'text-emerald-700' : value < 0 ? 'text-red-700' : 'text-slate-900';
  return (
    <div className="border-t border-blue-100 px-3 py-2.5 first:border-t-0 sm:border-t-0 sm:px-4">
      <dt className="text-[10px] font-bold uppercase tracking-[.06em] text-slate-500">{label}</dt>
      <dd className={`mt-1 text-base font-bold tabular-nums ${tone}`}>
        {signedMoney(value, currency)}
      </dd>
    </div>
  );
}

function MonthlyAnswer({
  data,
  calculationOpen,
  onShowCalculation,
}: {
  data: InsightsSummary;
  calculationOpen: boolean;
  onShowCalculation: () => void;
}) {
  const projectedClosing = data.kpis.projectedClosing;
  const availableFree = data.kpis.availableFree;
  const isOverLimit = availableFree < 0;
  // Con el límite sugerido, ambas fórmulas suelen producir la misma cifra.
  // Repetirla con dos títulos compite por atención sin aportar información.
  const hasDistinctBudgetMargin =
    Math.round(projectedClosing * 100) !== Math.round(availableFree * 100);

  return (
    <section
      aria-labelledby="monthly-answer-heading"
      className="overflow-hidden rounded-[20px] border border-slate-900 bg-slate-900 text-white shadow-[0_16px_34px_-18px_rgba(15,23,42,.65)]"
    >
      <div
        className={
          hasDistinctBudgetMargin
            ? 'grid lg:grid-cols-[minmax(0,1.45fr)_minmax(260px,.55fr)]'
            : undefined
        }
      >
        <div className="p-5 sm:p-6 lg:p-7">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[11px] font-bold uppercase tracking-[.08em] text-slate-300">
              Respuesta del mes
            </p>
            <StatusBadge status="Estimado" />
          </div>
          <h2
            id="monthly-answer-heading"
            className="mt-2 text-xl font-bold tracking-tight sm:text-2xl"
          >
            Te podría quedar al final del mes
          </h2>
          <p
            className={`mt-3 break-words text-[clamp(1.75rem,8.5vw,3rem)] font-bold leading-tight tracking-tight tabular-nums ${projectedClosing < 0 ? 'text-red-300' : 'text-white'}`}
          >
            {formatMoney(projectedClosing)}
          </p>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
            Considera tu ingreso base, compromisos, ahorro objetivo y gasto variable. Cambia cuando
            registras o ajustas información.
          </p>
          <button
            type="button"
            aria-label="Ver cómo se calcula"
            aria-controls="detalle-calculo-mensual"
            aria-expanded={calculationOpen}
            onClick={onShowCalculation}
            className="mt-4 inline-flex min-h-11 items-center text-sm font-semibold text-blue-200 hover:text-white"
          >
            Ver cómo se calcula →
          </button>
        </div>

        {hasDistinctBudgetMargin && (
          <aside className="border-t border-white/10 bg-white/[.06] p-5 lg:border-l lg:border-t-0 lg:p-6">
            <h3 className="text-sm font-semibold text-slate-200">
              {isOverLimit ? 'Exceso de tu límite de gasto' : 'Restante de tu límite de gasto'}
            </h3>
            <p
              className={`mt-3 break-words text-[clamp(1.6rem,8vw,1.875rem)] font-bold leading-tight tracking-tight tabular-nums ${isOverLimit ? 'text-red-300' : 'text-emerald-300'}`}
            >
              {formatMoney(isOverLimit ? Math.abs(availableFree) : availableFree)}
            </p>
            <p className="mt-2 text-xs leading-5 text-slate-300">
              Es margen de presupuesto, no saldo de tus cuentas. Usa tu límite configurado o el
              sugerido.
            </p>
          </aside>
        )}
      </div>
    </section>
  );
}

function MonthlyAction({ suggestion }: { suggestion: SuggestionWithAction }) {
  const style =
    suggestion.kind === 'warning'
      ? 'border-red-200 bg-red-50 text-red-950'
      : suggestion.kind === 'success'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
        : 'border-blue-200 bg-blue-50 text-blue-950';
  const linkStyle =
    suggestion.kind === 'warning'
      ? 'text-red-800 hover:text-red-950'
      : suggestion.kind === 'success'
        ? 'text-emerald-800 hover:text-emerald-950'
        : 'text-blue-800 hover:text-blue-950';
  const icon = suggestion.kind === 'warning' ? '!' : suggestion.kind === 'success' ? '✓' : 'i';

  return (
    <li className={`flex min-h-[128px] flex-col rounded-[16px] border p-4 ${style}`}>
      <div className="flex items-start gap-3">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-current text-xs font-bold"
          aria-hidden="true"
        >
          {icon}
        </span>
        <p className="text-sm leading-5">{suggestion.message}</p>
      </div>
      <Link
        to={suggestion.action.to}
        className={`mt-auto inline-flex min-h-11 items-center self-start pt-2 text-sm font-semibold ${linkStyle}`}
      >
        {suggestion.action.label} →
      </Link>
    </li>
  );
}

function Kpi({
  label,
  value,
  tone,
  to,
}: {
  label: string;
  value: number;
  tone: 'income' | 'expense' | 'savings';
  to?: string;
}) {
  const color =
    tone === 'income' ? 'text-income' : tone === 'expense' ? 'text-expense' : 'text-savings';

  const card = (
    <div
      className={`min-w-0 rounded-[16px] border border-slate-200/80 bg-white p-[14px] shadow-[0_1px_2px_rgba(15,23,42,.06),0_8px_20px_-12px_rgba(15,23,42,.15)] transition-all lg:p-[18px] ${to ? 'group-hover:-translate-y-0.5 group-hover:border-blue-200 group-hover:shadow-[0_12px_28px_-14px_rgba(37,99,235,.28)]' : ''}`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[.04em] text-slate-500">{label}</p>
      <p
        className={`mt-2 text-[24px] font-bold leading-none tracking-tight lg:text-[30px] ${color}`}
      >
        {formatMoney(value)}
      </p>
    </div>
  );
  if (!to) return card;

  return (
    <Link
      to={to}
      className="group block rounded-[16px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
      aria-label={`${label}. Abrir detalle.`}
    >
      {card}
    </Link>
  );
}

function SavingsCoach({
  saved,
  target,
  income,
  goal,
  topCategory,
}: {
  saved: number;
  target: number;
  income: number;
  goal?: InsightsSummary['goals'][number];
  topCategory?: string;
}) {
  const hasTarget = target > 0;
  const progress = hasTarget ? Math.max(0, Math.min(100, (saved / target) * 100)) : null;
  const missing = Math.max(0, target - saved);
  const savingRate = income > 0 ? (saved / income) * 100 : null;
  const onTrack = hasTarget && saved >= target;

  return (
    <section className="mt-5 overflow-hidden rounded-[20px] border border-blue-100 bg-[linear-gradient(118deg,#eff6ff_0%,#ffffff_52%,#ecfdf5_100%)] p-4 shadow-[0_12px_28px_-20px_rgba(37,99,235,.35)] sm:p-5">
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.45fr)_minmax(280px,.85fr)] lg:items-center">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[.09em] text-savings">
            Plan de ahorro
          </p>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <h2 className="text-xl font-bold tracking-tight text-slate-900">
              {!hasTarget
                ? 'Define cuánto quieres ahorrar'
                : onTrack
                  ? 'Vas al ritmo de tu ahorro'
                  : `Te faltan ${formatMoney(missing)} para tu objetivo`}
            </h2>
            {hasTarget && savingRate !== null && (
              <span className="text-sm font-semibold text-slate-500">
                · {savingRate.toFixed(1)}% de tus ingresos
              </span>
            )}
          </div>
          <p className="mt-1 max-w-2xl text-sm leading-5 text-slate-600">
            {target > 0
              ? `Has apartado ${formatMoney(saved)} de ${formatMoney(target)} planeados este mes.`
              : 'Aún no definiste un ahorro mensual. Una cantidad fija y automática es más fácil de sostener que ahorrar sólo lo que sobra.'}
          </p>
          {progress !== null && (
            <div className="mt-4">
              <div className="mb-1.5 flex justify-between text-xs font-semibold">
                <span className="text-slate-600">Avance del objetivo mensual</span>
                <span className={onTrack ? 'text-income' : 'text-savings'}>
                  {Math.round(progress)}%
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-blue-100">
                <div
                  className={`h-full rounded-full transition-all ${onTrack ? 'bg-income' : 'bg-savings'}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {hasTarget ? (
              <>
                <Link
                  to="/gastos-fijos"
                  state={{
                    automateSavings: true,
                    suggestedAmount: missing > 0 ? missing : target,
                    goalId: goal?.id,
                  }}
                  className="button button-primary"
                >
                  Automatizar ahorro
                </Link>
                <Link to="/metas" className="button button-secondary">
                  {goal ? 'Ver mi meta' : 'Crear una meta'}
                </Link>
              </>
            ) : (
              <Link to="/ajustes#ahorro" className="button button-primary">
                Definir mi objetivo
              </Link>
            )}
          </div>
        </div>
        <aside className="rounded-[16px] border border-white/80 bg-white/80 p-4 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-[.07em] text-slate-600">
            Siguiente decisión
          </p>
          <p className="mt-2 text-sm font-semibold leading-5 text-slate-800">
            {topCategory
              ? `Revisa ${topCategory}: es el rubro con mayor gasto del mes.`
              : 'Registra y categoriza tus movimientos para detectar oportunidades de ahorro.'}
          </p>
          <p className="mt-2 text-xs leading-5 text-slate-500">
            Antes de recortar, busca un gasto que puedas reducir sin afectar tus necesidades
            esenciales.
          </p>
          <Link
            to="/presupuesto"
            className="tap-target mt-3 inline-flex items-center text-sm font-semibold text-savings hover:text-blue-700"
          >
            ¿Cuánto puedo gastar hoy? →
          </Link>
        </aside>
      </div>
    </section>
  );
}

/** Muestra el fondo prioritario sin ocultar metas de largo plazo como vivienda. */
function GoalsOverview({
  primaryGoal,
  goals,
}: {
  primaryGoal: InsightsSummary['goals'][number];
  goals: InsightsSummary['goals'];
}) {
  const secondaryGoals = goals.filter((goal) => goal.id !== primaryGoal.id);
  const isEmergency = /fondo de emergencia/i.test(primaryGoal.name);
  const isFeasible = primaryGoal.feasibility === 'on_track' || primaryGoal.feasibility === 'tight';
  return (
    <div className="mt-3 grid gap-4 sm:grid-cols-[180px_minmax(0,1fr)] sm:items-center">
      <GoalRadial goal={primaryGoal} />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-800">
          {isEmergency
            ? 'Prioridad actual: fondo de emergencia'
            : `Meta prioritaria: ${primaryGoal.name}`}
        </p>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          {isEmergency
            ? 'Este dinero cubre imprevistos; no es el enganche ni una bolsa para compras cotidianas.'
            : 'La recomendación mensual se actualiza con el monto que falta y la fecha objetivo.'}
        </p>
        <div
          className={`mt-3 rounded-xl border px-3 py-2.5 text-xs leading-5 ${isFeasible ? 'border-emerald-100 bg-emerald-50/60 text-emerald-900' : 'border-amber-200 bg-amber-50/70 text-amber-900'}`}
        >
          <p className="font-semibold">
            Faltan {formatMoney(primaryGoal.remaining)} · {primaryGoal.monthsRemaining}{' '}
            {primaryGoal.monthsRemaining === 1 ? 'mes' : 'meses'} para la fecha meta
          </p>
          <p className="mt-0.5">
            {primaryGoal.achievableDate
              ? `Con el ahorro protegido actual, la alcanzarías aproximadamente el ${formatDate(primaryGoal.achievableDate)}.`
              : `Necesitas apartar ${formatMoney(primaryGoal.suggestedMonthly)}/mes para sostener el plan.`}
          </p>
        </div>
        {secondaryGoals.length > 0 && (
          <ul className="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-100 bg-slate-50/70 px-3">
            {secondaryGoals.slice(0, 2).map((goal) => (
              <li key={goal.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <span className="min-w-0 truncate font-medium text-slate-700">{goal.name}</span>
                <span className="shrink-0 text-right text-xs text-slate-500">
                  {Math.round(goal.progress * 100)}% · {formatMoney(goal.suggestedMonthly)}/mes
                </span>
              </li>
            ))}
          </ul>
        )}
        <Link
          to="/metas"
          className="tap-target mt-3 inline-flex items-center text-sm font-semibold text-savings hover:text-blue-700"
        >
          Ver y ajustar metas →
        </Link>
      </div>
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <SkeletonState label="Cargando panorama…" className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
      <Skeleton className="h-52" />
      <div className="grid gap-3 lg:grid-cols-3">
        <Skeleton className="h-72 lg:col-span-2" />
        <Skeleton className="h-72" />
      </div>
    </SkeletonState>
  );
}
